import { needsSpitermiteAttention } from '@next/arable-lib/crop';
import { RouterSelectors } from '@next/core-lib/router-store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CropAid } from 'src/app/crop-aid/crop-aid.model';
import { selectCropAids } from 'src/app/crop-aid/store/crop-aid.selectors';
import {
  SalesOrderPeriod,
  TreatmentType,
} from 'src/app/sales-order-period/models/sales-order-period.models';
import {
  selectSalesOrderPeriods,
  selectPastSalesOrderPeriods,
} from 'src/app/sales-order-period/store/sales-order-period.selectors';
import {
  CropFieldAdvice,
  ExecutedTreatmentWithNitrogenEffectivity,
  OrganicMatterTable,
  Template,
} from 'src/app/shared/models';
import { selectCropFieldAdvices } from 'src/app/store/selectors';

import { selectNitrogenEffectivityForAidAdvice } from './nitrogen-effectivity.selectors';
import { AdviceState } from '../models';

export const selectAdviceState = createFeatureSelector<AdviceState>('advice');

export const shouldShowSpidermiteChip = (cropId: number, ediCode: number) =>
  createSelector(
    selectAdviceState,
    (_: AdviceState) => needsSpitermiteAttention(cropId, ediCode));

export const selectSelectedCropFieldIds = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.selectedCropFieldIds || [],
);

export const selectEditingCropFieldAdviceId = createSelector(
  RouterSelectors.getRouteParam('cropFieldAdviceId'),
  (id: string) => (id ? parseInt(id, 10) : null),
);

export const selectEditingTreatmentType = createSelector(
  RouterSelectors.getRouteParam('treatmentType'),
  (treatmentType: string) =>
    Object.values(TreatmentType).includes(treatmentType as TreatmentType)
      ? (treatmentType as TreatmentType)
      : null,
);

export const selectEditingAdvice = createSelector(
  selectCropFieldAdvices,
  selectEditingCropFieldAdviceId,
  (cropFieldAdvices: CropFieldAdvice[], cropFieldId: number) => {
    const foundAdvice = !cropFieldAdvices
      ? null
      : cropFieldAdvices.find((cropFieldAdvice) => cropFieldAdvice.cropFieldId === cropFieldId);
    return foundAdvice ?? null;
  },
);

export const selectCropFieldAdviceById = (id: number) =>
  createSelector(selectCropFieldAdvices, (cropFieldAdvices) =>
    cropFieldAdvices.find((cropFieldAdvice) => cropFieldAdvice.cropFieldId === id),
  );

export const selectEditingCropFieldId = createSelector(
  selectEditingAdvice,
  (advice) => advice?.cropFieldId,
);

export const selectTemplate = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.template || null,
);

export const selectTemplateSchemas = createSelector(selectTemplate, (template: Template) =>
  template ? template.treatmentSchemas : [],
);

export const selectFertilizationSettings = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.fertilizationSettings || [],
);

export const selectFertilizationSetting = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.fertilizationSetting,
);

export const selectNitrogenCalculationSettingToggles = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.nitrogenCalculationSettingToggles,
);

export const selectNitrogenCalculationSettingTogglesByAdvices = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.nitrogenCalculationSettingTogglesByAdvices ?? [],
);

export const selectNitrogenCalculationSettingTogglesByAdviceId = (id: number) =>
  createSelector(
    selectNitrogenCalculationSettingTogglesByAdvices,
    (nitrogenCalculationSettingToggles) =>
      nitrogenCalculationSettingToggles?.find((toggle) => toggle.adviceId === id),
  );

export const shouldShowNitrogenCalculationToggles = createSelector(
  selectAdviceState,
  (store: AdviceState) =>
    store.nitrogenCalculationSettingToggles?.shouldShowGrasslandUsage === true ||
    store.nitrogenCalculationSettingToggles?.shouldShowPreviousCropCornToggle === true ||
    store.nitrogenCalculationSettingToggles?.shouldShowPreviousCropGrainToggle === true ||
    store.nitrogenCalculationSettingToggles?.shouldShowShouldSplitNormToggle === true ||
    store.nitrogenCalculationSettingToggles?.shouldShowNitrogenDifferentiationToggle === true ||
    store.nitrogenCalculationSettingToggles?.shouldShowAfterTornGrassToggle === true ||
    store.nitrogenCalculationSettingToggles?.shouldShowOrganicMatterToggle === true,
);

export const selectExecutedTreatments = createSelector(
  selectAdviceState,
  selectSalesOrderPeriods,
  selectPastSalesOrderPeriods,
  selectCropAids,
  (
    store: AdviceState,
    salesOrderPeriods: SalesOrderPeriod[],
    pastSalesOrderPeriods: SalesOrderPeriod[],
    cropAids: CropAid[],
  ): ExecutedTreatmentWithNitrogenEffectivity[] =>
    store.executedTreatments
      ? store.executedTreatments.map((treatment) => ({
        ...treatment,
        isAnExecutedTreatment: true,
        aidAdvices: treatment.aidAdvices.map((aidAdvice) => ({
          ...aidAdvice,
          ...selectNitrogenEffectivityForAidAdvice({
            periodId: treatment.periodId,
            aidAdvice,
            isAnExecutedTreatment: true,
          }).projector(salesOrderPeriods, pastSalesOrderPeriods, cropAids),
        })),
      }))
      : [],
);

export const selectRecommendedNutrientQuantityWithIds = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.recommendedNutrientQuantityWithIds,
);

export const selectLatestSoilAnalysis = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.latestSoilAnalysis,
);

export const selectOrganicMatterReport = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.organicMatterReport,
);

export const selectOrganicMatterTableReport = createSelector(
  selectOrganicMatterReport,
  (report) => {
    if (!report) {
      return [];
    }
    const tables: OrganicMatterTable[] = [];
    const allFieldsTable: OrganicMatterTable = {
      name: 'totalCropFields',
      translateName: true,
      totalHectares: report.totals.hectares,
      balance: report.totals.organicMatterBalance,
      balancePerHectare: report.totals.organicMatterBalancePerHectare,
      organicMatterItems: [
        {
          source: 'cropSupply',
          data: {
            total: report.totals.cropOrganicMatterSupply,
            perHectare: report.totals.cropOrganicMatterSupplyPerHectare,
          },
        },
        {
          source: 'fertilizerSupply',
          data: {
            total: report.totals.fertilizersOrganicMatterSupply,
            perHectare: report.totals.fertilizersOrganicMatterSupplyPerHectare,
          },
        },
        {
          source: 'breakdown',
          data: {
            total: report.totals.cropFieldOrganicMatterBreakdown,
            perHectare: report.totals.cropFieldOrganicMatterBreakdownPerHectare,
          },
        },
      ],
      metaData: { isTotals: true },
    };
    tables.push(allFieldsTable);

    report.cropFields.forEach((cropFieldReport) => {
      const cropFieldTable: OrganicMatterTable = {
        name: `${cropFieldReport.name} (${cropFieldReport.cropName})`,
        translateName: false,
        totalHectares: cropFieldReport.hectares,
        balance: cropFieldReport.organicMatterBalance,
        balancePerHectare: cropFieldReport.organicMatterBalancePerHectare,
        organicMatterItems: [],
        metaData: { cropFieldName: cropFieldReport.name, cropName: cropFieldReport.cropName },
      };
      cropFieldTable.organicMatterItems.push({
        source: 'cropSupply',
        data: {
          total: cropFieldReport.cropOrganicMatterSupply,
          perHectare: cropFieldReport.cropOrganicMatterSupplyPerHectare,
        },
      });
      cropFieldTable.organicMatterItems.push({
        source: 'fertilizerSupply',
        data: {
          total: cropFieldReport.fertilizersOrganicMatterSupply,
          perHectare: cropFieldReport.fertilizersOrganicMatterSupplyPerHectare,
        },
      });
      cropFieldTable.organicMatterItems.push({
        source: 'breakdown',
        data: {
          total: cropFieldReport.cropFieldOrganicMatterBreakdown,
          perHectare: cropFieldReport.cropFieldOrganicMatterBreakdownPerHectare,
        },
      });
      tables.push(cropFieldTable);
    });
    return tables;
  },
);

export const isPrefilling = createSelector(
  selectAdviceState,
  (store) => store.isPrefilling || false,
);

export const selectCustomerFertilizerStock = createSelector(
  selectAdviceState,
  (store: AdviceState) => store.customerFertilizerStock?.fertilizerStock || [],
);
